/*
  Pack quantity rules
  v1.0.1 - re-written 2026-08-31 to give the GitHub sync a changed file to pick up.
  -------------------
  Forces specific variants to be bought in fixed multiples (e.g. packs of 10).
  The config map is built by snippets/pack-quantity-rules.liquid into
  window.PackQtyRules, keyed by variant id:

    { "44726431809690": { size: 10, sku: "USI 061253" } }

  Covers:
    - product page quantity input: min, step and starting value all set to the
      pack size, so the stepper runs 10 / 20 / 30 and the number shown matches
      what actually lands in the cart
    - an explanatory note in Dawn's .quantity__rules slot under the selector
    - cart page and cart drawer quantity inputs (min / step / snapping, no note:
      the cart grid column is too narrow to take one without crushing the field)
    - add-to-cart submits, including quick-add on collection pages
    - direct /cart/add.js, /cart/change.js and /cart/update.js calls from the theme

  Note on finding the quantity field: in Dawn the quantity input lives OUTSIDE
  the <form> element and is bound to it with a form="..." attribute. Never look
  for it with form.querySelector - use input.form / form.elements instead.
*/
(function () {
  'use strict';

  var applying = false; // guards against the MutationObserver re-entering

  function allRules() {
    return window.PackQtyRules || {};
  }

  // Cart line keys look like "44726431809690:9a8b7c" - keep the variant id only.
  function ruleFor(id) {
    if (id === null || id === undefined) return null;
    var key = String(id).trim();
    var colon = key.indexOf(':');
    if (colon > -1) key = key.slice(0, colon);
    return allRules()[key] || null;
  }

  // 0 is always allowed - that is how a cart line gets removed.
  function snapUp(qty, size) {
    qty = parseInt(qty, 10);
    if (isNaN(qty) || qty <= 0) return 0;
    if (qty <= size) return size;
    return Math.ceil(qty / size) * size;
  }

  // Assign through the native setter so frameworks/custom elements watching the
  // property still see the change.
  function setValue(input, value, notify) {
    if (String(input.value) === String(value)) return;
    var desc = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
    if (desc && desc.set) desc.set.call(input, value);
    else input.value = value;
    if (notify) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ---------- 1. The note under the product page selector ---------- */

  function noteText(size) {
    return 'Sold in packs of ' + size + '. Quantity changes in steps of ' + size + '.';
  }

  function showNote(input, rule) {
    var wrapper = input.closest('.product-form__quantity, .product-form__input--quantity');

    // Dawn ships a caption slot right under the stepper for exactly this.
    var slot = wrapper && wrapper.querySelector('.quantity__rules');
    if (slot) {
      if (slot.dataset.packNote !== String(rule.size)) {
        slot.dataset.packNote = String(rule.size);
        slot.textContent = noteText(rule.size);
      }
      slot.classList.remove('hidden');
      slot.hidden = false;
      return;
    }

    // Fallback for templates without that slot.
    var host = input.closest('quantity-input') || input.parentElement;
    if (!host || !host.parentElement) return;
    var existing = host.parentElement.querySelector('.pack-qty-note');
    if (existing) {
      existing.textContent = noteText(rule.size);
      return;
    }
    var note = document.createElement('p');
    note.className = 'pack-qty-note';
    note.textContent = noteText(rule.size);
    note.style.cssText = [
      'display:block',
      'width:100%',
      'flex-basis:100%',
      'grid-column:1 / -1',
      'box-sizing:border-box',
      'margin:0.8rem 0 0',
      'font-size:1.3rem',
      'line-height:1.4',
      'opacity:0.75',
      'overflow-wrap:break-word'
    ].join(';');
    host.insertAdjacentElement('afterend', note);
  }

  function hideNote(input) {
    var wrapper = input.closest('.product-form__quantity, .product-form__input--quantity');
    var slot = wrapper && wrapper.querySelector('.quantity__rules');
    if (slot && slot.dataset.packNote) {
      slot.textContent = '';
      slot.dataset.packNote = '';
      slot.classList.add('hidden');
    }
    var host = input.closest('quantity-input') || input.parentElement;
    var fallback = host && host.parentElement && host.parentElement.querySelector('.pack-qty-note');
    if (fallback) fallback.remove();
  }

  /* ---------- 2. Quantity inputs ---------- */

  function decorate(input, rule, opts) {
    opts = opts || {};
    input.setAttribute('min', rule.size);
    input.setAttribute('step', rule.size);
    input.setAttribute('data-min', rule.size);
    input.dataset.packSize = rule.size;

    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = 0;

    if (opts.isCart) {
      // Never touch 0 in the cart - that is a line removal in progress.
      if (current > 0) setValue(input, snapUp(current, rule.size), false);
    } else {
      // Product page: the field should read a full pack from the start.
      setValue(input, snapUp(current || rule.size, rule.size), true);
      showNote(input, rule);
    }

    if (!input.dataset.packBound) {
      input.dataset.packBound = '1';
      input.addEventListener('change', function () {
        if (applying) return;
        var size = parseInt(input.dataset.packSize, 10);
        if (!size) return;
        var v = parseInt(input.value, 10);
        if (isNaN(v) || v <= 0) return;
        var s = snapUp(v, size);
        if (s !== v) setValue(input, s, false);
      });
    }
  }

  function undecorate(input) {
    if (!input.dataset.packSize) return;
    input.setAttribute('min', '1');
    input.setAttribute('step', '1');
    input.setAttribute('data-min', '1');
    delete input.dataset.packSize;
    hideNote(input);
  }

  function isInCart(el) {
    return !!el.closest('cart-drawer, cart-items, #main-cart-items, .cart-item, .cart-drawer');
  }

  // Cart line inputs carry the variant id on the element itself.
  function applyToCartInputs() {
    document.querySelectorAll('input[data-quantity-variant-id]').forEach(function (input) {
      var rule = ruleFor(input.getAttribute('data-quantity-variant-id'));
      if (rule) decorate(input, rule, { isCart: true });
    });
  }

  // Product page and quick add. input.form resolves the form= attribute, so this
  // works even though the stepper sits outside the <form> in Dawn.
  function applyToProductQuantities() {
    document.querySelectorAll('input[name="quantity"]').forEach(function (input) {
      if (input.hasAttribute('data-quantity-variant-id')) return; // handled above
      var form = input.form;
      if (!form) return;
      var idEl = form.elements ? form.elements['id'] : null;
      if (idEl && idEl.length && !idEl.value) idEl = idEl[0];
      if (!idEl || !idEl.value) return;

      var rule = ruleFor(idEl.value);
      if (rule) decorate(input, rule, { isCart: isInCart(input) });
      else undecorate(input);
    });
  }

  function applyAll() {
    if (applying) return;
    applying = true;
    try {
      applyToCartInputs();
      applyToProductQuantities();
    } catch (e) {
      console.error('[pack-quantity-rules]', e);
    } finally {
      applying = false;
    }
  }

  /* ---------- 3. Guard add-to-cart submits ---------- */

  document.addEventListener(
    'submit',
    function (evt) {
      var form = evt.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      var idEl = form.elements ? form.elements['id'] : null;
      if (idEl && idEl.length && !idEl.value) idEl = idEl[0];
      if (!idEl) return;
      var rule = ruleFor(idEl.value);
      if (!rule) return;

      var qtyEl = form.elements ? form.elements['quantity'] : null;
      if (qtyEl && qtyEl.length && qtyEl.value === undefined) qtyEl = qtyEl[0];
      var qty = qtyEl ? parseInt(qtyEl.value, 10) : 1;
      if (isNaN(qty)) qty = 1;
      var snapped = snapUp(qty, rule.size);
      if (snapped === qty) return;

      if (qtyEl) {
        setValue(qtyEl, snapped, false);
      } else {
        // Quick add with no quantity field at all - inject one.
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'quantity';
        hidden.value = snapped;
        form.appendChild(hidden);
      }
    },
    true // capture, so this runs before the theme's own submit handler
  );

  /* ---------- 4. Guard the cart's own AJAX calls ---------- */

  function lineVariantId(line) {
    var lines = window.PackQtyCartLines || [];
    var idx = parseInt(line, 10);
    if (isNaN(idx) || idx < 1) return null;
    return lines[idx - 1] || null;
  }

  function fixPlainObject(obj) {
    // Shapes: { id, quantity } | { line, quantity } | { updates: { variantId: qty } }
    if (obj.updates && typeof obj.updates === 'object') {
      Object.keys(obj.updates).forEach(function (key) {
        var rule = ruleFor(key);
        if (rule) obj.updates[key] = snapUp(obj.updates[key], rule.size);
      });
    }
    if (obj.quantity !== undefined) {
      var target = obj.id !== undefined ? obj.id : lineVariantId(obj.line);
      var r = ruleFor(target);
      if (r) obj.quantity = snapUp(obj.quantity, r.size);
    }
    return obj;
  }

  function fixBody(body) {
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var rule = ruleFor(body.get('id'));
        if (rule) {
          var q = body.get('quantity');
          body.set('quantity', snapUp(q === null ? 1 : q, rule.size));
        }
        return body;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        var r2 = ruleFor(body.get('id'));
        if (r2) {
          var q2 = body.get('quantity');
          body.set('quantity', snapUp(q2 === null ? 1 : q2, r2.size));
        }
        return body;
      }
      if (typeof body === 'string') {
        return JSON.stringify(fixPlainObject(JSON.parse(body)));
      }
    } catch (e) {
      console.error('[pack-quantity-rules] could not inspect request body', e);
    }
    return body;
  }

  var CART_WRITE = /\/cart\/(add|change|update)(\.js)?(\?|$)/;

  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (resource, init) {
      try {
        var url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
        if (CART_WRITE.test(url) && init && init.body) {
          init.body = fixBody(init.body);
        }
      } catch (e) {
        console.error('[pack-quantity-rules]', e);
      }
      return nativeFetch.apply(this, arguments);
    };
  }

  /* ---------- 5. Keep working after section re-renders ---------- */

  function boot() {
    applyAll();

    // The product section re-renders on variant change and after add-to-cart,
    // which resets the input back to 1. Re-assert shortly after those settle.
    [50, 250, 800, 1500].forEach(function (ms) {
      window.setTimeout(applyAll, ms);
    });

    var pending = null;
    var observer = new MutationObserver(function () {
      if (applying || pending) return;
      pending = window.requestAnimationFrame(function () {
        pending = null;
        applyAll();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Variant switches swap the hidden [name=id] value without re-rendering.
    document.addEventListener('change', function (evt) {
      if (applying) return;
      var t = evt.target;
      if (t && t.name === 'id') applyToProductQuantities();
    });

    // Dawn's pub/sub, when present.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        if (PUB_SUB_EVENTS.variantChange) subscribe(PUB_SUB_EVENTS.variantChange, applyAll);
        if (PUB_SUB_EVENTS.cartUpdate) subscribe(PUB_SUB_EVENTS.cartUpdate, applyAll);
      }
    } catch (e) {
      /* pub/sub not available - the observer already covers us */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
